import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { CreditCard, Loader2, AlertCircle, Calendar } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

function formatApiError(detail, fallback = "Something went wrong.") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || JSON.stringify(detail);
}

/**
 * BillingCard — used on the Branding page (and the agent Dashboard overview).
 * Shows the agent's current subscription state + opens the Stripe Customer Portal
 * so they can update card / view invoices / cancel without leaving the app.
 */
export default function BillingCard({ compact = false }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  if (!user || user.role !== "agent") return null;

  const onManage = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.href });
      if (!data?.url) throw new Error("No portal URL");
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not open billing portal."));
    } finally {
      setLoading(false);
    }
  };

  const isActive = user.subscription_status === "active";
  const renew = user.subscription_renew_at ? new Date(user.subscription_renew_at) : null;
  const daysLeft = renew ? Math.ceil((renew.getTime() - Date.now()) / 86400000) : null;

  return (
    <div className={`bg-white border border-sg-line rounded-3xl ${compact ? "p-5" : "p-7"}`} data-testid="billing-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center text-sg-gold-deep">
          <CreditCard className="w-4 h-4" />
        </div>
        <div>
          <div className="overline text-sg-gold-deep">SUBSCRIPTION</div>
          <h3 className="font-display text-2xl text-sg-navy leading-tight">
            {isActive ? "Active" : user.subscription_status === "past_due" ? "Past due" : user.subscription_status === "canceled" ? "Canceled" : "Pending"}
            <span className="ml-2 font-mono text-sm text-sg-mute">· ₹999 / month</span>
          </h3>
        </div>
      </div>
      {isActive && renew && (
        <div className="flex items-center gap-2 text-sm text-sg-mute" data-testid="billing-renews">
          <Calendar className="w-4 h-4" />
          Renews on <strong className="text-sg-navy">{renew.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</strong>
          {daysLeft != null && (
            <span className={`text-xs ${daysLeft <= 5 ? "text-amber-700" : "text-sg-mute"}`}>· in {daysLeft} days</span>
          )}
        </div>
      )}
      {!isActive && (
        <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2" data-testid="billing-inactive">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          {user.subscription_status === "pending"
            ? "Your subscription is pending. Complete checkout to publish your own branded portal."
            : "Subscription is inactive. Renew to keep your own branded portal live."}
        </div>
      )}
      <div className="mt-5 flex flex-wrap gap-3">
        <Button
          onClick={onManage}
          disabled={loading}
          className="rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white h-11 px-5"
          data-testid="billing-manage-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
          {isActive ? "Manage subscription" : "Subscribe"}
        </Button>
        {!isActive && (
          <a href="/subscribe" className="inline-flex items-center text-sm text-sg-gold-deep border-b border-sg-gold h-11" data-testid="billing-subscribe-link">
            Go to plan page
          </a>
        )}
      </div>
      <p className="mt-4 text-xs text-sg-mute">
        Opens Stripe&apos;s secure portal — update card, view invoices, or cancel any time.
      </p>
    </div>
  );
}
